import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useState } from 'react';
import cn from 'classnames';
import { Kebab } from '../../../../../icons';
import { getTestIds } from '../../../../../utils/TestIds';
import { useBiContextGetter } from '../../../../BiTracking/BiContext';
import { Divider } from '../../../../Divider';
import { DropdownMenu } from '../../../../DropdownMenu';
import { DropdownSubMenu } from '../../../../DropdownMenu/DropdownSubMenu';
import { IconButton } from '../../../../IconButton';
import { Box, Row } from '../../../../Layout';
import { TextButton } from '../../../../TextButton';
import { CARD_ACTION_ICON_SIZE, PRIMARY_ACTION_ICON_SIZE, KEBAB_ICON_SIZE } from '../../table.constants';
import styles from '../cells.module.scss';
import { ActionMenuItem } from './ActionMenuItem';
export function ActionsCell(param) {
    let { actions, row, variant = 'row', testId } = param;
    var _ref, _ref1;
    var _actions_inline, _actions_menu, _actions_menuTitle, _actions_menuSubtitle;
    const testIds = getTestIds(testId);
    const biContext = useBiContextGetter();
    // WHY: the stylesheet keeps the reveal and pill while the menu is open and the row is no longer hovered.
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const pillRef = useRef(null);
    const inline = (_ref = (_actions_inline = actions.inline) === null || _actions_inline === void 0 ? void 0 : _actions_inline.call(actions, row)) !== null && _ref !== void 0 ? _ref : [];
    const menuActions = (_ref1 = (_actions_menu = actions.menu) === null || _actions_menu === void 0 ? void 0 : _actions_menu.call(actions, row)) !== null && _ref1 !== void 0 ? _ref1 : [];
    // WHY: a card shows actions in its menu, except a lone action with no menu, which is one tap as a button.
    const foldsInlineIntoMenu = variant === 'card' && !(inline.length === 1 && menuActions.length === 0);
    const menuEntries = foldsInlineIntoMenu ? [
        ...inline,
        ...menuActions
    ] : menuActions;
    const inlineActions = foldsInlineIntoMenu ? [] : inline;
    const isLoneCardIcon = variant === 'card' && inlineActions.length === 1 && inlineActions[0].icon != null;
    if (inline.length === 0 && menuEntries.length === 0) return null;
    return /*#__PURE__*/ _jsx(Row, {
        alignItems: "center",
        justifyContent: "end",
        className: cn(styles.actions, {
            [styles.rowActions]: variant === 'row'
        }),
        children: /*#__PURE__*/ _jsxs(Row, {
            ref: pillRef,
            alignItems: "center",
            gap: 8,
            className: cn(styles.actionsPill, 'dt-actions-pill', {
                [styles.startsWithKebab]: inlineActions.length === 0,
                [styles.endsWithKebab]: menuEntries.length > 0,
                [styles.loneCardIcon]: isLoneCardIcon
            }),
            "data-menu-open": isMenuOpen ? 'true' : undefined,
            children: [
                inlineActions.length > 0 && /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "center",
                    gap: 8,
                    className: "dt-reveal",
                    children: [
                        inlineActions.map((action, index)=>/*#__PURE__*/ _jsxs(React.Fragment, {
                                children: [
                                    index > 0 && /*#__PURE__*/ _jsx(Divider, {
                                        orientation: "vertical",
                                        margin: 0,
                                        className: styles.actionsDivider,
                                        testId: testIds.inlineDivider(index)
                                    }),
                                    isLoneCardIcon && action.icon != null ? // WHY: on a card the lone action is one tap as its icon, the way the legacy phone card showed Edit.
                                    /*#__PURE__*/ _jsx(IconButton, {
                                        bi: action.bi,
                                        size: "smallMedium",
                                        "aria-label": action.label,
                                        disabled: action.disabled,
                                        intent: action.intent,
                                        "data-heapid": action.heapId,
                                        onClick: ()=>action.onClick(biContext),
                                        testId: testIds.inline(index),
                                        children: /*#__PURE__*/ _jsx(Box, {
                                            as: "span",
                                            testId: testIds.inlineIcon(index),
                                            children: /*#__PURE__*/ _jsx(action.icon, {
                                                size: CARD_ACTION_ICON_SIZE
                                            })
                                        })
                                    }) : /*#__PURE__*/ _jsx(TextButton, {
                                        bi: action.bi,
                                        skin: "primary",
                                        variant: "paragraph2",
                                        weight: "normal",
                                        disabled: action.disabled,
                                        intent: action.intent,
                                        "data-heapid": action.heapId,
                                        className: styles.primaryAction,
                                        prefixIcon: action.icon != null ? /*#__PURE__*/ _jsx(Box, {
                                            as: "span",
                                            testId: testIds.inlineIcon(index),
                                            children: /*#__PURE__*/ _jsx(action.icon, {
                                                size: PRIMARY_ACTION_ICON_SIZE
                                            })
                                        }) : undefined,
                                        onClick: (_event, biCallerContext)=>action.onClick(biCallerContext),
                                        testId: testIds.inline(index),
                                        children: action.label
                                    })
                                ]
                            }, "".concat(action.label, "-").concat(index))),
                        menuEntries.length > 0 && /*#__PURE__*/ _jsx(Divider, {
                            orientation: "vertical",
                            margin: 0,
                            className: styles.actionsDivider,
                            testId: testIds.divider()
                        })
                    ]
                }),
                menuEntries.length > 0 && /*#__PURE__*/ _jsx(DropdownMenu, {
                    anchorRef: pillRef,
                    // WHY: the menu paper carries its own 8px margin.
                    gap: -4,
                    onOpenChange: setIsMenuOpen,
                    target: /*#__PURE__*/ _jsx(IconButton, {
                        bi: actions.menuBi,
                        size: "medium",
                        aspect: "square",
                        pressed: isMenuOpen,
                        className: styles.kebabButton,
                        "data-heapid": actions.menuHeapId,
                        testId: testIds.trigger(),
                        children: /*#__PURE__*/ _jsx(Kebab, {
                            size: KEBAB_ICON_SIZE
                        })
                    }),
                    mobile: actions.menuTitle != null || actions.menuSubtitle != null ? {
                        title: (_actions_menuTitle = actions.menuTitle) === null || _actions_menuTitle === void 0 ? void 0 : _actions_menuTitle.call(actions, row),
                        subtitle: (_actions_menuSubtitle = actions.menuSubtitle) === null || _actions_menuSubtitle === void 0 ? void 0 : _actions_menuSubtitle.call(actions, row)
                    } : undefined,
                    testId: testIds.menu(),
                    children: menuEntries.map((action, index)=>action.subItems != null && action.subItems.length > 0 ? /*#__PURE__*/ _jsx(DropdownSubMenu, {
                            bi: action.bi,
                            label: action.label,
                            iconStart: action.icon != null ? /*#__PURE__*/ _jsx(action.icon, {
                                size: PRIMARY_ACTION_ICON_SIZE
                            }) : undefined,
                            disabled: action.disabled,
                            testId: testIds.action(index),
                            children: action.subItems.map((subItem, subIndex)=>/*#__PURE__*/ _jsx(ActionMenuItem, {
                                    action: subItem,
                                    testId: "".concat(testIds.action(index), "-sub-").concat(subIndex)
                                }, "".concat(subItem.label, "-").concat(subIndex)))
                        }, "".concat(action.label, "-").concat(index)) : /*#__PURE__*/ _jsx(ActionMenuItem, {
                            action: action,
                            testId: testIds.action(index)
                        }, "".concat(action.label, "-").concat(index)))
                })
            ]
        })
    });
}
